<template>
  <main class="page-shell">
    <section class="page-heading page-heading--activity">
      <div>
        <h1>Our shares</h1>
        <p>See file activity and manage the links you have created.</p>
      </div>
      <button class="btn-ui btn-ui--secondary" type="button" :disabled="activityLoading" @click="loadShares">
        <svg class="ui-icon" :class="{ 'ui-icon--spin': activityLoading }" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7"/>
        </svg>
        <span>{{ activityLoading ? 'Refreshing…' : 'Refresh activity' }}</span>
      </button>
    </section>

    <section class="metric-strip" aria-label="Share summary">
      <article>
        <span class="metric-strip__dot metric-strip__dot--blue" aria-hidden="true"></span>
        <div>
          <span>Shares</span>
          <strong>{{ summary.share_count }}</strong>
          <small>Links created</small>
        </div>
      </article>
      <article>
        <span class="metric-strip__dot metric-strip__dot--purple" aria-hidden="true"></span>
        <div>
          <span>Files</span>
          <strong>{{ summary.file_count }}</strong>
          <small>Currently shared</small>
        </div>
      </article>
      <article>
        <span class="metric-strip__dot metric-strip__dot--green" aria-hidden="true"></span>
        <div>
          <span>Downloads</span>
          <strong>{{ summary.download_count }}</strong>
          <small>Total recorded</small>
        </div>
      </article>
    </section>

    <div v-if="activityError" class="notice notice--error page-notice" role="alert">
      <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 8v5m0 3h.01M10.3 4.3 2.7 17.5A1.7 1.7 0 0 0 4.2 20h15.6a1.7 1.7 0 0 0 1.5-2.5L13.7 4.3a2 2 0 0 0-3.4 0Z"/>
      </svg>
      <span>{{ activityError }}</span>
    </div>

    <div v-if="activitySuccess" class="notice notice--success page-notice" role="status">
      <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m5 12 4 4L19 6"/>
      </svg>
      <span>{{ activitySuccess }}</span>
    </div>

    <section class="surface-card activity-card" aria-labelledby="recent-shares-title">
      <div class="activity-card__header">
        <div>
          <h2 id="recent-shares-title">Recent share activity</h2>
          <p>Expand a share to review its files and latest downloads.</p>
        </div>
        <span v-if="lastRefreshedAt">Updated {{ formatTimeOnly(lastRefreshedAt) }}</span>
      </div>

      <div v-if="activityLoading && !shares.length" class="empty-state" role="status">
        <span class="empty-state__icon" aria-hidden="true">
          <svg class="ui-icon ui-icon--spin" viewBox="0 0 24 24">
            <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7"/>
          </svg>
        </span>
        <h3>Loading your shares</h3>
        <p>Checking the latest download activity.</p>
      </div>

      <div v-else-if="!shares.length" class="empty-state">
        <span class="empty-state__icon" aria-hidden="true">
          <svg class="ui-icon" viewBox="0 0 24 24">
            <path d="M7 3h7l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h4"/>
          </svg>
        </span>
        <h3>No shares yet</h3>
        <p>Your uploaded files and download activity will appear here.</p>
        <button class="btn-ui btn-ui--primary" type="button" @click="$emit('navigate', '/')">Create a share</button>
      </div>

      <div v-else class="share-list">
        <article v-for="share in shares" :key="share.share_id" class="share-row" :class="{ 'share-row--open': isExpanded(share.share_id) }">
          <button
            class="share-row__summary"
            type="button"
            :aria-expanded="String(isExpanded(share.share_id))"
            :aria-controls="'share-details-' + share.share_id"
            @click="toggleShare(share.share_id)"
          >
            <span class="share-row__file-icon" aria-hidden="true">
              <svg class="ui-icon" viewBox="0 0 24 24">
                <path d="M7 3h7l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h4"/>
              </svg>
            </span>

            <span class="share-row__name">
              <strong>{{ shareTitle(share) }}</strong>
              <small>Shared {{ formatDate(share.created_at) }} · {{ formatBytes(share.total_size) }}</small>
            </span>

            <span class="share-row__metric">
              <small>Files</small>
              <strong>{{ share.file_count }}</strong>
            </span>

            <span class="share-row__metric">
              <small>Downloads</small>
              <strong>{{ share.download_count }}</strong>
            </span>

            <span class="share-row__last">
              <small>Last download</small>
              <strong>{{ share.last_downloaded_at ? formatDateTime(share.last_downloaded_at) : 'Not downloaded yet' }}</strong>
            </span>

            <span class="share-row__expand" aria-hidden="true">
              <svg class="ui-icon" viewBox="0 0 24 24">
                <path d="m7 10 5 5 5-5"/>
              </svg>
            </span>
          </button>

          <div v-if="isExpanded(share.share_id)" :id="'share-details-' + share.share_id" class="share-details">
            <div class="share-details__link-row">
              <div class="share-details__link-copy">
                <span>Share link</span>
                <strong>{{ share.share_url }}</strong>
              </div>
              <div class="share-details__actions">
                <button class="btn-ui btn-ui--secondary btn-ui--compact" type="button" @click="copyShareLink(share)">
                  <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 8h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h2"/>
                  </svg>
                  <span>{{ copyStatus[share.share_id] || 'Copy link' }}</span>
                </button>
                <a class="btn-ui btn-ui--secondary btn-ui--compact" :href="share.share_url" target="_blank" rel="noopener noreferrer">Open link</a>
                <button class="btn-ui btn-ui--danger-quiet btn-ui--compact" type="button" :disabled="isDeleting(share.share_id)" @click="removeShare(share)">
                  <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 7h16m-10 4v5m4-5v5M9 7l1-3h4l1 3m3 0-1 13H7L6 7"/>
                  </svg>
                  <span>{{ isDeleting(share.share_id) ? 'Deleting…' : 'Delete share' }}</span>
                </button>
              </div>
            </div>

            <div class="share-details__grid">
              <section class="detail-panel" aria-label="Files in this share">
                <div class="detail-panel__header">
                  <div>
                    <h3>Files</h3>
                    <p>Download status for each file.</p>
                  </div>
                  <span>{{ share.files.length }}</span>
                </div>

                <div class="detail-file-list">
                  <div v-for="file in share.files" :key="file.file_id" class="detail-file">
                    <span class="detail-file__icon" aria-hidden="true">
                      <svg class="ui-icon" viewBox="0 0 24 24">
                        <path d="M7 3h7l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h4"/>
                      </svg>
                    </span>
                    <div class="detail-file__name">
                      <strong>{{ file.name }}</strong>
                      <span>{{ formatBytes(file.size) }}</span>
                    </div>
                    <div class="detail-file__downloads">
                      <strong>{{ file.download_count }}</strong>
                      <span>{{ Number(file.download_count) === 1 ? 'download' : 'downloads' }}</span>
                    </div>
                    <span class="detail-file__last">{{ file.last_downloaded_at ? formatDateTime(file.last_downloaded_at) : 'Never downloaded' }}</span>
                  </div>
                </div>
              </section>

              <section class="detail-panel" aria-label="Latest download activity">
                <div class="detail-panel__header">
                  <div>
                    <h3>Latest downloads</h3>
                    <p>Most recent activity for this link.</p>
                  </div>
                  <span>{{ share.downloads.length }}</span>
                </div>

                <div v-if="!share.downloads.length" class="detail-empty">
                  <svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 4v12m0 0 4-4m-4 4-4-4M5 20h14"/>
                  </svg>
                  <strong>No downloads yet</strong>
                  <span>Activity appears here after a file is downloaded.</span>
                </div>

                <div v-else class="event-list">
                  <div v-for="event in share.downloads" :key="eventKey(event)" class="event-row">
                    <span class="event-row__dot" aria-hidden="true"></span>
                    <div>
                      <strong>{{ event.file_name }}</strong>
                      <span>Downloaded {{ formatDateTime(event.downloaded_at) }}</span>
                      <small>{{ describeDevice(event.user_agent) }}</small>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import { deleteShare, fetchShareActivity } from '../services/shareApi'

export default {
  name: 'ShareActivity',

  props: {
    auth: {
      type: Object,
      required: true
    }
  },

  data: function () {
    return {
      activityLoading: false,
      activityError: '',
      activitySuccess: '',
      shares: [],
      summary: {
        share_count: 0,
        file_count: 0,
        download_count: 0
      },
      expandedShares: {},
      copyStatus: {},
      deletingShares: {},
      refreshTimer: null,
      lastRefreshedAt: ''
    }
  },

  mounted: async function () {
    await this.loadShares()
    this.startAutoRefresh()
  },

  beforeDestroy: function () {
    this.stopAutoRefresh()
  },

  methods: {
    apiMessage: function (response, fallback) {
      return response && response.data && response.data.message
        ? response.data.message
        : fallback
    },

    loadShares: async function () {
      if (this.activityLoading) {
        return
      }

      if (!this.auth || !this.auth.token) {
        this.activityError = 'Your saved login could not be found. Return to Upload and sign in again.'
        return
      }

      this.activityLoading = true
      this.activityError = ''

      try {
        var response = await fetchShareActivity(this.auth.token)

        if (!response.data || !response.data.success) {
          this.activityError = this.apiMessage(response, 'Could not load share activity.')
          return
        }

        var incoming = Array.isArray(response.data.shares) ? response.data.shares : []
        this.shares = incoming.map(function (share) {
          return Object.assign({}, share, {
            files: Array.isArray(share.files) ? share.files : [],
            downloads: Array.isArray(share.downloads) ? share.downloads : []
          })
        })
        this.summary = response.data.summary || {
          share_count: this.shares.length,
          file_count: 0,
          download_count: 0
        }
        this.lastRefreshedAt = new Date().toISOString()
        this.expandRequestedShare()
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.activityError = this.apiMessage(error.response, 'Your share activity could not be verified. Return to Upload to re-check your login.')
        } else {
          this.activityError = this.apiMessage(error.response, 'Could not load share activity.')
        }
      } finally {
        this.activityLoading = false
      }
    },

    expandRequestedShare: function () {
      var params = new URLSearchParams(window.location.search)
      var requested = String(params.get('share') || '')
      if (!requested) {
        return
      }

      var exists = this.shares.some(function (share) {
        return share.share_id === requested
      })
      if (exists) {
        this.$set(this.expandedShares, requested, true)
      }
    },

    toggleShare: function (shareId) {
      this.$set(this.expandedShares, shareId, !this.expandedShares[shareId])
    },

    isExpanded: function (shareId) {
      return Boolean(this.expandedShares[shareId])
    },

    shareTitle: function (share) {
      if (share.files.length === 1) {
        return share.files[0].name
      }
      if (share.files.length > 1) {
        return share.files[0].name + ' +' + (share.files.length - 1) + ' more'
      }
      return 'Share ' + share.share_id
    },

    copyShareLink: async function (share) {
      if (!share || !share.share_url) {
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(share.share_url)
        } else {
          var textarea = document.createElement('textarea')
          try {
            textarea.value = share.share_url
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            if (!document.execCommand('copy')) {
              throw new Error('Copy failed')
            }
          } finally {
            if (textarea.parentNode) {
              textarea.parentNode.removeChild(textarea)
            }
          }
        }

        this.$set(this.copyStatus, share.share_id, 'Copied')
        window.setTimeout(function () {
          this.$set(this.copyStatus, share.share_id, '')
        }.bind(this), 1800)
      } catch (error) {
        this.$set(this.copyStatus, share.share_id, 'Copy manually')
      }
    },

    isDeleting: function (shareId) {
      return Boolean(this.deletingShares[shareId])
    },

    removeShare: async function (share) {
      if (!share || !share.share_id || this.isDeleting(share.share_id)) {
        return
      }

      var confirmed = window.confirm(
        'Delete this share and all uploaded files? Its link and download history will be permanently removed.'
      )
      if (!confirmed) {
        return
      }

      this.$set(this.deletingShares, share.share_id, true)
      this.activityError = ''
      this.activitySuccess = ''

      try {
        var response = await deleteShare(this.auth.token, share.share_id)
        if (!response.data || !response.data.success) {
          this.activityError = this.apiMessage(response, 'Could not delete this share.')
          return
        }

        this.$delete(this.expandedShares, share.share_id)
        this.$delete(this.copyStatus, share.share_id)
        this.shares = this.shares.filter(function (item) {
          return item.share_id !== share.share_id
        })
        this.activitySuccess = response.data.message || 'The share was deleted.'
        await this.loadShares()
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.activityError = this.apiMessage(error.response, 'The activity API could not verify this change.')
        } else {
          this.activityError = this.apiMessage(error.response, 'Could not delete this share.')
        }
      } finally {
        this.$set(this.deletingShares, share.share_id, false)
      }
    },

    startAutoRefresh: function () {
      this.stopAutoRefresh()
      this.refreshTimer = window.setInterval(function () {
        this.loadShares()
      }.bind(this), 30000)
    },

    stopAutoRefresh: function () {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    eventKey: function (event) {
      return event.download_id || event.file_id + '-' + event.downloaded_at
    },

    formatBytes: function (bytes) {
      var number = Number(bytes || 0)
      if (!number) {
        return '0 B'
      }
      var units = ['B', 'KB', 'MB', 'GB', 'TB']
      var index = Math.min(Math.floor(Math.log(number) / Math.log(1024)), units.length - 1)
      var value = number / Math.pow(1024, index)
      return value.toFixed(index === 0 || value >= 10 ? 0 : 1) + ' ' + units[index]
    },

    parseDate: function (value) {
      if (!value) {
        return null
      }
      var date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    },

    formatDate: function (value) {
      var date = this.parseDate(value)
      return date
        ? date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
        : 'Unknown date'
    },

    formatDateTime: function (value) {
      var date = this.parseDate(value)
      return date
        ? date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : 'Unknown time'
    },

    formatTimeOnly: function (value) {
      var date = this.parseDate(value)
      return date ? date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : ''
    },

    describeDevice: function (userAgent) {
      var ua = String(userAgent || '')
      if (!ua) {
        return 'Browser details unavailable'
      }

      var browser = 'Browser'
      var os = 'Device'

      if (/Edg\//.test(ua)) browser = 'Edge'
      else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browser = 'Chrome'
      else if (/Firefox\//.test(ua)) browser = 'Firefox'
      else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari'

      if (/iPhone/.test(ua)) os = 'iPhone'
      else if (/iPad/.test(ua)) os = 'iPad'
      else if (/Android/.test(ua)) os = 'Android'
      else if (/Macintosh|Mac OS X/.test(ua)) os = 'macOS'
      else if (/Windows/.test(ua)) os = 'Windows'
      else if (/Linux/.test(ua)) os = 'Linux'

      return browser + ' · ' + os
    }
  }
}
</script>
